/**
 * Permissions Utilities - Sistema de validação de permissões e features
 * Baseado no JWT enriquecido e estrutura RBAC
 */
/**
 * Permissões simples por role
 */
export const ROLE_PERMISSIONS = {
    superadmin: [
        'dashboard',
        'settings',
        'users',
        'roles',
        'accounts',
        'analytics',
        'docs',
        'instagram',
        'system'
    ],
    admin: [
        'dashboard',
        'settings',
        'users',
        'analytics',
        'docs',
        'instagram'
    ],
    moderator: [
        'dashboard',
        'settings',
        'analytics',
        'docs',
        'instagram'
    ],
    user: [
        'dashboard',
        'settings',
        'analytics',
        'docs'
    ]
};
/**
 * Features simples por plano
 */
export const PLAN_FEATURES = {
    free: [
        'dashboard',
        'analytics',
        'docs'
    ],
    basic: [
        'dashboard',
        'analytics',
        'docs',
        'instagram',
        'export'
    ],
    pro: [
        'dashboard',
        'analytics',
        'docs',
        'instagram',
        'export',
        'advanced_reports',
        'users'
    ],
    enterprise: [
        'dashboard',
        'analytics',
        'docs',
        'instagram',
        'export',
        'advanced_reports',
        'users',
        'integrations',
        'custom_features'
    ]
};
/**
 * Verifica se um usuário tem uma role específica
 */
export const hasRole = (userRole, requiredRole) => {
    const roleHierarchy = {
        user: 1,
        moderator: 2,
        admin: 3,
        superadmin: 4
    };
    const userLevel = roleHierarchy[userRole] || 0;
    const requiredLevel = roleHierarchy[requiredRole] || 0;
    return userLevel >= requiredLevel;
};
/**
 * Verifica se um usuário tem uma role específica (exata)
 */
export const hasExactRole = (userRole, requiredRole) => {
    return userRole === requiredRole;
};
/**
 * Verifica se um usuário tem uma permissão específica baseada na role
 */
export const hasPermission = (userRole, permission) => {
    const permissions = ROLE_PERMISSIONS[userRole] || [];
    return permissions.includes(permission);
};
/**
 * Verifica se um usuário tem acesso a uma feature específica
 */
export const hasFeature = (userFeatures, featureKey) => {
    return userFeatures.includes(featureKey);
};
/**
 * Verifica se um plano inclui uma feature específica
 */
export const planIncludesFeature = (planType, featureKey) => {
    const features = PLAN_FEATURES[planType] || [];
    return features.includes(featureKey);
};
/**
 * Verifica se um usuário pode acessar um recurso específico
 */
export const canAccess = (userRole, userFeatures, resource, action = 'read') => {
    const permission = `${resource}.${action}`;
    // Verificar permissão por role
    const hasRolePermission = hasPermission(userRole, permission);
    // Verificar se tem a feature necessária (se aplicável)
    const hasRequiredFeature = hasFeature(userFeatures, resource);
    // Para recursos básicos, apenas role é suficiente
    const basicResources = ['profile', 'dashboard', 'settings', 'docs'];
    if (basicResources.includes(resource)) {
        return hasRolePermission;
    }
    // Para outros recursos, precisa de role E feature
    return hasRolePermission && hasRequiredFeature;
};
/**
 * Verifica se um usuário está na mesma conta (multi-tenant)
 */
export const isInSameAccount = (userAccountId, targetAccountId) => {
    return userAccountId === targetAccountId;
};
/**
 * Verifica se um usuário pode gerenciar outro usuário
 */
export const canManageUser = (managerRole, managerAccountId, targetRole, targetAccountId) => {
    // Superadmin pode gerenciar qualquer usuário
    if (managerRole === 'superadmin') {
        return true;
    }
    // Outros roles só podem gerenciar usuários da mesma conta
    if (!isInSameAccount(managerAccountId, targetAccountId)) {
        return false;
    }
    // Admin pode gerenciar moderator e user
    if (managerRole === 'admin') {
        return ['moderator', 'user'].includes(targetRole);
    }
    // Moderator pode gerenciar apenas user
    if (managerRole === 'moderator') {
        return targetRole === 'user';
    }
    return false;
};
// ===== REDUX HELPER FUNCTIONS =====
/**
 * Função helper para verificar roles com Redux state
 */
export const checkRole = (userRole, requiredRoles, exact = false) => {
    if (!userRole)
        return false;
    const roles = Array.isArray(requiredRoles) ? requiredRoles : [requiredRoles];
    return roles.some(role => exact ? hasExactRole(userRole, role) : hasRole(userRole, role));
};
/**
 * Função helper para verificar permissões com Redux state
 */
export const checkPermission = (userRole, permissions) => {
    if (!userRole)
        return false;
    const perms = Array.isArray(permissions) ? permissions : [permissions];
    return perms.some(permission => hasPermission(userRole, permission));
};
/**
 * Função helper para verificar features com Redux state
 */
export const checkFeature = (userFeatures, features) => {
    if (!userFeatures)
        return false;
    const feats = Array.isArray(features) ? features : [features];
    return feats.some(feature => hasFeature(userFeatures, feature));
};
/**
 * Função helper para verificar acesso a recursos com Redux state
 */
export const checkAccess = (userRole, userFeatures, resource, action = 'read') => {
    if (!userRole || !userFeatures)
        return false;
    return canAccess(userRole, userFeatures, resource, action);
};
/**
 * Função helper para verificar gerenciamento de usuários com Redux state
 */
export const checkUserManagement = (managerRole, managerAccountId, targetRole, targetAccountId) => {
    if (!managerRole || !managerAccountId)
        return false;
    return canManageUser(managerRole, managerAccountId, targetRole, targetAccountId);
};
/**
 * Função helper para verificar mesma conta com Redux state
 */
export const checkSameAccount = (userAccountId, targetAccountId) => {
    if (!userAccountId)
        return false;
    return isInSameAccount(userAccountId, targetAccountId);
};
/**
 * Obtém todas as permissões de um usuário
 */
export const getUserPermissions = (userRole) => {
    return ROLE_PERMISSIONS[userRole] || [];
};
/**
 * Obtém todas as features disponíveis para um plano
 */
export const getPlanFeatures = (planType) => {
    return PLAN_FEATURES[planType] || [];
};
/**
 * Verifica se um usuário pode fazer upgrade/downgrade de plano
 */
export const canChangePlan = (userRole) => {
    return hasPermission(userRole, 'accounts.write') || userRole === 'admin';
};
/**
 * Verifica se um usuário pode acessar configurações avançadas
 */
export const canAccessAdvancedSettings = (userRole, userFeatures) => {
    return hasRole(userRole, 'admin') && hasFeature(userFeatures, 'settings');
};
/**
 * Verifica se um usuário pode ver dados de outras contas (superadmin only)
 */
export const canViewAllAccounts = (userRole) => {
    return userRole === 'superadmin';
};
/**
 * Classe utilitária para validação de permissões
 */
export class PermissionValidator {
    constructor(userRole, userFeatures, userAccountId) {
        Object.defineProperty(this, "userRole", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: userRole
        });
        Object.defineProperty(this, "userFeatures", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: userFeatures
        });
        Object.defineProperty(this, "userAccountId", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: userAccountId
        });
    }
    hasRole(role) {
        return hasRole(this.userRole, role);
    }
    hasPermission(permission) {
        return hasPermission(this.userRole, permission);
    }
    hasFeature(feature) {
        return hasFeature(this.userFeatures, feature);
    }
    canAccess(resource, action = 'read') {
        return canAccess(this.userRole, this.userFeatures, resource, action);
    }
    canManageUser(targetRole, targetAccountId) {
        return canManageUser(this.userRole, this.userAccountId, targetRole, targetAccountId);
    }
    isInSameAccount(targetAccountId) {
        return isInSameAccount(this.userAccountId, targetAccountId);
    }
}
/**
 * Cria um validador de permissões a partir do JWT payload
 */
export const createPermissionValidator = (jwtPayload) => {
    return new PermissionValidator(jwtPayload.role, jwtPayload.features || [], jwtPayload.accountId);
};
/**
 * Debug: Mostra informações de permissões no console
 */
export const debugPermissions = (userRole, userFeatures) => {
    console.group('🔐 Permissions Debug');
    console.log('🎭 Role:', userRole);
    console.log('📋 Permissões:', getUserPermissions(userRole));
    console.log('⚡ Features:', userFeatures);
    // Testar algumas permissões comuns
    const commonTests = [
        { resource: 'users', action: 'read' },
        { resource: 'users', action: 'write' },
        { resource: 'analytics', action: 'read' },
        { resource: 'settings', action: 'write' }
    ];
    console.log('🧪 Testes de acesso:');
    commonTests.forEach(test => {
        const access = canAccess(userRole, userFeatures, test.resource, test.action);
        console.log(`  ${test.resource}.${test.action}: ${access ? '✅' : '❌'}`);
    });
    console.groupEnd();
};
// ===== SIMPLE PERMISSION CHECKERS =====
/**
 * Verifica se o usuário pode acessar uma seção específica das configurações
 */
export const canAccessSettingsSection = (userRole, section) => {
    if (!userRole)
        return false;
    const sectionAccess = {
        profile: ['user', 'moderator', 'admin', 'superadmin'],
        users: ['admin', 'superadmin'],
        roles: ['superadmin'],
        accounts: ['superadmin']
    };
    return sectionAccess[section].includes(userRole);
};
/**
 * Verifica se o usuário pode acessar features específicas do plano
 */
export const canAccessPlanFeature = (userPlan, feature) => {
    if (!userPlan)
        return false;
    const planFeatures = getPlanFeatures(userPlan);
    return planFeatures.includes(feature);
};
/**
 * Informações simples do plano (sem limites complexos)
 */
export const getPlanInfo = (userPlan) => {
    if (!userPlan)
        return null;
    const planInfo = {
        free: {
            name: 'Free',
            features: ['Dashboard básico', 'Analytics básico', 'Documentação']
        },
        basic: {
            name: 'Basic',
            features: ['Dashboard completo', 'Analytics completo', 'Instagram', 'Exportação']
        },
        pro: {
            name: 'Pro',
            features: ['Relatórios avançados', 'Gestão de usuários', 'Recursos premium']
        },
        enterprise: {
            name: 'Enterprise',
            features: ['Recursos ilimitados', 'Integrações customizadas', 'Suporte prioritário']
        }
    };
    return planInfo[userPlan] || planInfo.free;
};
